import moment from 'moment';
import React from 'react';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {TEXT_HIGHLIGHT_THEME, TextHighlightReact} from 'common/components/text-highlight';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {emitTrackEvent} from 'common/util/dom';
import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {useCurrentProject} from 'common/contexts/react/current-project-context';

import styles from './index.module.styl';
import css from './index.module.styl?inline';

function getImplDaysLeft(implementationDeadline: string, utcOffset?: number): number {
  const impleDdlMoment = moment(implementationDeadline);
  // Add 1 to include the current day in the count
  const daysLeft = impleDdlMoment.diff(moment().utcOffset(utcOffset ? utcOffset : 0), `days`) + 1;
  return daysLeft >= 0 ? daysLeft : 0;
}

function getDaysLeftTheme(daysLeft: number): TEXT_HIGHLIGHT_THEME {
  if (daysLeft <= 10) {
    return TEXT_HIGHLIGHT_THEME.DELETED; // Red
  } else if (daysLeft <= 30) {
    return TEXT_HIGHLIGHT_THEME.FAILED; // Orange
  }
  return TEXT_HIGHLIGHT_THEME.INFORMATION;
}

function getDaysLeftText(daysLeft: number): string {
  return `${daysLeft} Day${daysLeft === 1 ? `` : `s`} Left`;
}

function onClickedAddBillingInfo(daysLeft: number): void {
  trackClickStartSendingDataEvent(daysLeft);
  window.open(`https://docs.mixpanel.com/docs/tutorials/implement/send-your-data`, `_blank`);
}

function trackClickStartSendingDataEvent(daysLeft: number): void {
  emitTrackEvent(window, {
    eventName: `Startup Start Sending Data Clicked`,
    props: {
      'Days Left': daysLeft,
    },
  });
}

function CountdownPanelCard() {
  const currentProject = useCurrentProject();
  const showImplementationDeadline = currentProject.get().value?.showImplementationDeadline;
  const implementationDeadline = currentProject.get().value?.implementationDeadline;
  const utcOffset = currentProject.get().value?.utcOffset;

  if (!showImplementationDeadline) return null;
  if (!implementationDeadline) return null;

  const daysLeft = getImplDaysLeft(implementationDeadline, utcOffset);
  if (daysLeft <= 0) return null;

  return (
    <div className={styles.trialInfo}>
      <div className={styles.trialDetails}>
        Implementation Period{` `}
        <TextHighlightReact className='.days-left' theme={getDaysLeftTheme(daysLeft)} text={getDaysLeftText(daysLeft)}>
          <TooltipReact
            text={`Implement by ${moment(implementationDeadline).format(`MMMM Do`)} to stay in the startup program.\n[Learn more →](https://help.mixpanel.com/hc/en-us/articles/360043474691-Mixpanel-s-Startup-Program)`}
            placement={Placement.Right}
          />
        </TextHighlightReact>
      </div>
      <ButtonReact
        className={styles.billingBtn}
        elref='billingBtn'
        theme={ButtonTheme.Secondary}
        notFocusable
        styleOverride={`a.mp-button-container.mp-button-secondary { background-color: transparent; }`}
        onClick={() => onClickedAddBillingInfo(daysLeft)}
      >
        Start Sending Data
      </ButtonReact>
    </div>
  );
}

export const CountdownPanelCardReact = withStyles(CountdownPanelCard, css);
export const CountdownPanelCardEl = react2panel(`mp-countdown-panel-card`, CountdownPanelCardReact);
